/**
 * The client-side router's `basename`, derived from the browser location.
 *
 * This is the one place that turns a tenant slug into a router basename. It
 * calls {@link tenantSlugFromPath} rather than re-parsing the path itself, so
 * the router and `EntraSessionGate`'s own `tenantSlugFromPath` call can never
 * disagree about what a path segment means.
 *
 * @module server/tenant-basename
 */

import { tenantSlugFromPath } from "./tenant-addressing";

/**
 * The router basename for the current browser location.
 *
 * Path-addressed tenants (`/chevron/...`) get `/chevron` so every route in
 * the tree can be written without a tenant prefix. Host-addressed
 * deployments, and any path that addresses no tenant, get `/`, which is
 * `createBrowserRouter`'s own default and matches routes unprefixed.
 *
 * @param pathname - Browser location path, e.g. `window.location.pathname`.
 * @returns The router basename.
 */
export function computeTenantBasename(pathname: string): string {
  const slug = tenantSlugFromPath(pathname);
  return slug ? `/${slug}` : "/";
}
